/**
 * The leaderboard's data hook (VERA-260) — one ranked breakdown, one card.
 *
 * ## The area and scan-status filters are merged HERE, not at the call site
 *
 * `useAnalyticsAreaParam` / `useAnalyticsScanStatusParam` both exist for the same reason
 * and both state it at length: a page-global filter that call sites have to remember is
 * a filter call sites forget (§42's referrer prop reached 2 of 37). Consuming them here
 * makes the leaderboard area-filtered and scan-status-filtered by construction, with no
 * per-card wiring — which is the property those hooks were written to give a NEW card,
 * and this is the first new card since.
 *
 * ⚠️ `useAnalyticsScanStatusParam`'s header once said it was injected into
 * `useAnalyticsScansMap` "and NOWHERE else, because scans-map is the only endpoint that
 * can honour it". The CONSTRAINT is real and unchanged — the filter resolves from
 * `scans.invalid` / `.tampered`, so it needs a query that reads the `scans` table rather
 * than `certificate_action_logs` — but the endpoint count is not: VRG-452 moved the
 * tiles' and the chart's scan counts onto `scans`, so that header now lists them too.
 * `top-influencers` is such a query, so it qualifies rather than being an exception.
 * `/roi` is the one that still does not — see that header for why.
 */
import { useQuery } from '@tanstack/react-query'

import type { TopInfluencerDimension } from '@repo/types'

import { analyticsTopInfluencersApi, type TopInfluencersParams } from 'services'

import {
  useAnalyticsAreaParam,
  useAnalyticsScanStatusParam,
} from './useAnalyticsAreaParam'

/** Everything the caller supplies. The two page-global filters are added below. */
export type UseAnalyticsTopInfluencersParams = Omit<
  TopInfluencersParams,
  'area' | 'scan_statuses'
> & { dimension: TopInfluencerDimension }

export const useAnalyticsTopInfluencers = (
  params: UseAnalyticsTopInfluencersParams,
  options?: { enabled?: boolean },
) => {
  const area = useAnalyticsAreaParam()
  const scanStatuses = useAnalyticsScanStatusParam()

  const request: TopInfluencersParams = {
    ...params,
    ...(scanStatuses ? { scan_statuses: scanStatuses } : {}),
    ...(area ? { area } : {}),
  }

  return useQuery({
    /*
      The whole request IS the key, so every filter on the page invalidates the cache by
      construction — including the two merged above. Spelling out a subset would be the
      bug `AnalyticsScansMapCache` documents: a card reading an answer computed for a
      different question, which looks like stale data rather than like a wrong one.
    */
    queryKey: ['analytics', 'top-influencers', request],
    queryFn: () => analyticsTopInfluencersApi.get(request),
    enabled: options?.enabled ?? true,
    /*
      Keep the previous ranking on screen while a new one loads, rather than blanking to
      a skeleton on every filter nudge. §19's `isLoading` vs `isFetching` split: the
      skeleton is for the FIRST load, and a table that empties itself each time the date
      range moves reads as broken.
    */
    placeholderData: previous => previous,
    staleTime: 60 * 1000,
  })
}
